import { useEffect } from 'react';

/**
 * Hook that pins the app container to the actual visible viewport area.
 * 
 * Problem: On iOS Safari, when the virtual keyboard opens the browser performs a
 * "visual viewport scroll" to bring the focused input into view. This scroll is
 * separate from document scroll and cannot be prevented by CSS `overflow: hidden`.
 * The result is the header scrolling off-screen.
 * 
 * Solution: This hook tracks two values from the Visual Viewport API:
 *   --vh         → visualViewport.height  (visible height, excluding keyboard)
 *   --vv-offset  → visualViewport.offsetTop (how far the browser scrolled)
 * 
 * The app container uses `position: fixed; top: var(--vv-offset); height: var(--vh)`
 * so it always stays pinned to the visible area regardless of browser scrolling.
 * 
 * On desktop, offsetTop is always 0 and height equals window.innerHeight, so
 * behavior is identical to a normal full-height layout.
 */
export function useViewportHeight(): void {
  useEffect(() => {
    let prevHeight = window.visualViewport?.height ?? window.innerHeight;

    const update = () => {
      const vv = window.visualViewport;
      const height = vv?.height ?? window.innerHeight;
      const offset = vv?.offsetTop ?? 0;

      document.documentElement.style.setProperty('--vh', `${height}px`);
      document.documentElement.style.setProperty('--vv-offset', `${offset}px`);

      // Notify listeners when viewport shrinks (keyboard opened)
      if (height < prevHeight - 50) {
        window.dispatchEvent(new CustomEvent('viewport-shrink'));
      }
      prevHeight = height;
    };

    // Set initial values
    update();

    // Listen to visualViewport events (fires when keyboard opens/closes, pinch zoom, etc.)
    const vv = window.visualViewport;
    if (vv) {
      vv.addEventListener('resize', update);
      vv.addEventListener('scroll', update);
    }

    // Also listen to window resize as fallback (desktop resize, orientation change)
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);

    return () => {
      if (vv) {
        vv.removeEventListener('resize', update);
        vv.removeEventListener('scroll', update);
      }
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
    };
  }, []);
}
